/**
 * CartContext — Panier global VAD (LocalStorage-backed, cross-cartes).
 *
 * Structure d'un item :
 *   {
 *     product_id, card_id, card_qr_token, card_name,
 *     name, description, photo_url,
 *     unit_price_cents, tva_rate, is_discountable,
 *     tracks_stock, stock_qty,
 *     qty
 *   }
 *
 * Coupons appliqués : { [card_id]: { code, coupon_id, name, discount_cents } }
 * (résolus côté serveur via /api/vad/multi/preview au recalcul.)
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "briouse:vad:cart:v1";

const CartContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], coupons: {} };
    const parsed = JSON.parse(raw);
    return {
      items: Array.isArray(parsed?.items) ? parsed.items : [],
      coupons: parsed?.coupons && typeof parsed.coupons === "object" ? parsed.coupons : {},
    };
  } catch {
    return { items: [], coupons: {} };
  }
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

export function CartProvider({ children }) {
  const [state, setState] = useState(loadFromStorage);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { saveToStorage(state); }, [state]);

  const addItem = useCallback((item, qty = 1) => {
    setState((s) => {
      const idx = s.items.findIndex(
        (it) => it.product_id === item.product_id && it.card_id === item.card_id
      );
      const items = [...s.items];
      if (idx >= 0) {
        items[idx] = { ...items[idx], qty: items[idx].qty + qty };
      } else {
        items.push({ ...item, qty });
      }
      return { ...s, items };
    });
  }, []);

  const removeItem = useCallback((product_id, card_id) => {
    setState((s) => ({
      ...s,
      items: s.items.filter((it) => !(it.product_id === product_id && it.card_id === card_id)),
    }));
  }, []);

  const updateQty = useCallback((product_id, card_id, qty) => {
    setState((s) => {
      const items = s.items
        .map((it) => (it.product_id === product_id && it.card_id === card_id
          ? { ...it, qty: Math.max(0, qty) }
          : it))
        .filter((it) => it.qty > 0);
      return { ...s, items };
    });
  }, []);

  const clearCart = useCallback(() => {
    setState({ items: [], coupons: {} });
  }, []);

  const setCouponForCard = useCallback((card_id, couponData) => {
    setState((s) => {
      const coupons = { ...s.coupons };
      if (couponData) coupons[card_id] = couponData;
      else delete coupons[card_id];
      return { ...s, coupons };
    });
  }, []);

  const clearCouponForCard = useCallback((card_id) => {
    setState((s) => {
      const coupons = { ...s.coupons };
      delete coupons[card_id];
      return { ...s, coupons };
    });
  }, []);

  // Invalider les coupons quand le panier change (les totaux serveur revalident)
  const totalItems = useMemo(
    () => state.items.reduce((sum, it) => sum + it.qty, 0),
    [state.items]
  );

  const grossTotalCents = useMemo(
    () => state.items.reduce((sum, it) => sum + it.unit_price_cents * it.qty, 0),
    [state.items]
  );

  // Groupement par carte pour l'affichage
  const itemsByCard = useMemo(() => {
    const map = new Map();
    for (const it of state.items) {
      if (!map.has(it.card_id)) {
        map.set(it.card_id, {
          card_id: it.card_id,
          card_name: it.card_name,
          card_qr_token: it.card_qr_token,
          items: [],
          gross_cents: 0,
        });
      }
      const g = map.get(it.card_id);
      g.items.push(it);
      g.gross_cents += it.unit_price_cents * it.qty;
    }
    return Array.from(map.values());
  }, [state.items]);

  const value = useMemo(() => ({
    items: state.items,
    coupons: state.coupons,
    totalItems,
    grossTotalCents,
    itemsByCard,
    drawerOpen, setDrawerOpen,
    addItem, removeItem, updateQty, clearCart,
    setCouponForCard, clearCouponForCard,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, totalItems, grossTotalCents, itemsByCard, drawerOpen]);
  // Note : les callbacks (addItem, removeItem, updateQty, clearCart, setCouponForCard,
  // clearCouponForCard, setDrawerOpen) sont créés avec useCallback([]) ou setState —
  // leur référence est stable pour toute la vie du provider, donc les omettre du
  // tableau de deps est sûr et évite les re-render superflus.

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
